// frontend/src/pages/purchaseorder/ConvertOrderToGRN.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  CheckCircle,
  Loader2,
  Package,
  Plus,
  Receipt,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./ConvertOrderToGRN.css";

const url = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PAYMENT_METHODS = [
  { key: "credit", name: "Credit (Pay Later)", accounts: [] },
  { key: "cash", name: "Cash", accounts: [] },
  { key: "bank", name: "Bank", accounts: [] },
  { key: "mobile_money", name: "Mobile Money", accounts: [] },
  { key: "other", name: "Other", accounts: [] },
];

const ConvertOrderToGRN = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [itemsToReceive, setItemsToReceive] = useState([]);

  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [allPaymentAccounts, setAllPaymentAccounts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Payment rows
  const [existingPayments, setExistingPayments] = useState([]);
  const [newPayments, setNewPayments] = useState([]);
  const [existingPaymentTotal, setExistingPaymentTotal] = useState(0);
  const [newPaymentTotal, setNewPaymentTotal] = useState(0);

  // Editable shipping / other charges
  const [shippingCost, setShippingCost] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);

  // Order-level discount / tax / manual discount
  const [purchaseAdjustments, setPurchaseAdjustments] = useState({
    discount_id: "",
    tax_id: "",
    manual_discount: "",
  });

  const [totals, setTotals] = useState({
    subTotal: 0,
    selectedDiscountAmount: 0,
    manualDiscountAmount: 0,
    totalDiscount: 0,
    taxableAmount: 0,
    selectedTaxAmount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
    totalAmountPaid: 0,
    netAmount: 0,
  });

  const getCompanyId = () => {
    if (userType === "company" && company) {
      return company._id || company;
    }
    return null;
  };

  const roundMoney = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

  const normalizeMethodKey = (value) =>
    String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

  // ---------- Payment methods helpers ----------
  const normalizePaymentMethodsResponse = (payload) => {
    const methods = [];
    const seen = new Set();
    const addMethod = (key, name, accounts = []) => {
      const normalizedKey = normalizeMethodKey(key || name);
      if (!normalizedKey || seen.has(normalizedKey)) return;
      methods.push({
        key: normalizedKey,
        name: name || key,
        accounts: Array.isArray(accounts) ? accounts.filter(Boolean) : [],
      });
      seen.add(normalizedKey);
    };

    const methodArrays = [
      payload?.methods, payload?.paymentMethods, payload?.payment_methods,
      payload?.paymentTypes, payload?.payment_types,
    ];

    methodArrays.forEach((array) => {
      if (!Array.isArray(array)) return;
      array.forEach((method) => {
        addMethod(
          method?.key || method?.method || method?.code || method?.name || method?.paymentstype,
          method?.name || method?.label || method?.paymentstype || method?.method || method?.key,
          method?.accounts || method?.posting_accounts || method?.account_options || [],
        );
      });
    });

    [
      ["cash", "Cash", payload?.cashAccounts || payload?.cash_accounts],
      ["bank", "Bank", payload?.bankAccounts || payload?.bank_accounts],
      ["mobile_money", "Mobile Money", payload?.mobileMoneyAccounts || payload?.mobile_money_accounts || payload?.mobileAccounts],
      ["other", "Other", payload?.otherAccounts || payload?.other_accounts || payload?.generalAccounts],
    ].forEach(([key, name, accounts]) => {
      if (Array.isArray(accounts) && accounts.length > 0) addMethod(key, name, accounts);
    });

    [
      ["credit", "Credit (Pay Later)"],
      ["cash", "Cash"],
      ["bank", "Bank"],
      ["mobile_money", "Mobile Money"],
      ["other", "Other"],
    ].forEach(([key, name]) => { if (!seen.has(key)) addMethod(key, name, []); });

    return methods;
  };

  const classifyFallbackAccount = (account) => {
    const name = String(account?.account_name || account?.name || account?.label || "").toLowerCase();
    const code = String(account?.account_code || "").trim();
    const paymentType = String(
      account?.paymentstype?.paymentstype || account?.paymentTypeDetails?.paymentstype || "",
    ).toLowerCase();
    const combined = `${name} ${paymentType}`;
    if (/evc|edahab|mobile|wallet|zaad|sahal/.test(combined)) return "mobile_money";
    if (/bank|checking|saving|current account/.test(combined)) return "bank";
    if (/cash|petty cash|cash on hand/.test(combined)) return "cash";
    if (code.startsWith("100")) return "cash";
    if (code.startsWith("101")) return "bank";
    return "other";
  };

  const buildMethodsFromAccounts = (accounts = []) => {
    const grouped = { cash: [], bank: [], mobile_money: [], other: [] };
    accounts.forEach((account) => {
      const key = classifyFallbackAccount(account);
      grouped[key].push({
        _id: account._id || account.value,
        account_code: account.account_code || "",
        account_name: account.account_name || account.name || account.label || "Account",
        balance: Number(account.balance || 0),
      });
    });
    return DEFAULT_PAYMENT_METHODS.map((method) => ({
      ...method,
      accounts: method.key === "credit" ? [] : grouped[method.key] || [],
    }));
  };

  const fetchFallbackPaymentAccounts = async (companyId) => {
    const endpoints = [
      `${url}/api/account/payment-methods`,
      `${url}/api/account/dropdown`,
    ];
    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(endpoint, {
          params: { company_id: companyId, type: "Asset" },
        });
        const responseData = response.data?.data;
        const accounts = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.accounts) ? responseData.accounts
          : Array.isArray(response.data?.accounts) ? response.data.accounts : [];
        if (accounts.length > 0) return buildMethodsFromAccounts(accounts);
      } catch (fallbackError) {
        console.warn(
          `Payment-account fallback failed for ${endpoint}:`,
          fallbackError.response?.data?.message || fallbackError.message,
        );
      }
    }
    return DEFAULT_PAYMENT_METHODS;
  };

  const fetchPaymentMethods = async () => {
    const companyId = getCompanyId();
    if (!companyId) {
      setPaymentMethods(DEFAULT_PAYMENT_METHODS);
      return DEFAULT_PAYMENT_METHODS;
    }

    try {
      const response = await axios.get(`${url}/api/purchaseorders/payment-methods`, {
        params: { company_id: companyId },
      });
      const options = response.data?.data || {};
      const normalized = normalizePaymentMethodsResponse(options);

      setDiscounts(Array.isArray(options.discounts) ? options.discounts : []);
      setTaxes(Array.isArray(options.taxes) ? options.taxes : []);

      const mergedMethods = DEFAULT_PAYMENT_METHODS.map((defaultMethod) => {
        const returned = normalized.find(
          (method) => normalizeMethodKey(method.key) === defaultMethod.key,
        );
        return returned
          ? {
              ...defaultMethod,
              name: returned.name || defaultMethod.name,
              accounts: Array.isArray(returned.accounts) ? returned.accounts : [],
            }
          : defaultMethod;
      });

      const hasPostingAccount = mergedMethods.some(
        (method) => method.key !== "credit" && method.accounts.length > 0,
      );

      const finalMethods = hasPostingAccount
        ? mergedMethods
        : await fetchFallbackPaymentAccounts(companyId);

      const uniqueAccounts = [];
      const seen = new Set();
      finalMethods.forEach((method) => {
        method.accounts.forEach((account) => {
          const accountId = String(account._id || account.value || "");
          if (!accountId || seen.has(accountId)) return;
          seen.add(accountId);
          uniqueAccounts.push(account);
        });
      });

      setPaymentMethods(finalMethods);
      setAllPaymentAccounts(uniqueAccounts);
      return finalMethods;
    } catch (error) {
      const fallbackMethods = await fetchFallbackPaymentAccounts(companyId);
      const uniqueAccounts = fallbackMethods.flatMap((method) => method.accounts || []);
      setPaymentMethods(fallbackMethods);
      setAllPaymentAccounts(uniqueAccounts);
      return fallbackMethods;
    }
  };

  const getAccountsForMethod = (methodKey) => {
    const normalizedKey = normalizeMethodKey(methodKey);
    return paymentMethods.find((method) => normalizeMethodKey(method.key) === normalizedKey)?.accounts || [];
  };

  const getImmediatePaymentMethods = () =>
    paymentMethods.filter(
      (method) =>
        normalizeMethodKey(method.key) !== "credit" &&
        Array.isArray(method.accounts) &&
        method.accounts.length > 0,
    );

  const findPaymentAccount = (payment) => {
    const rawId =
      payment?.account_id?._id || payment?.account_id ||
      payment?.account?._id || payment?.account || "";
    return allPaymentAccounts.find((account) => String(account._id) === String(rawId));
  };

  // Allocation helper — proportional by line gross, last line gets remainder
  const allocateDocumentAmount = (amount, lineIndex, sourceItems = itemsToReceive) => {
    const grossValues = sourceItems.map(
      (row) => roundMoney((Number(row.quantity) || 0) * (Number(row.unit_price) || 0)),
    );
    const totalGross = grossValues.reduce((sum, v) => sum + v, 0);
    const safeAmount = Math.max(0, Number(amount) || 0);

    if (safeAmount <= 0 || totalGross <= 0 || lineIndex < 0) return 0;

    if (lineIndex === grossValues.length - 1) {
      const allocatedBefore = grossValues
        .slice(0, lineIndex)
        .reduce(
          (sum, g) => sum + roundMoney((safeAmount * g) / totalGross),
          0,
        );
      return Math.max(0, roundMoney(safeAmount - allocatedBefore));
    }

    return roundMoney((safeAmount * grossValues[lineIndex]) / totalGross);
  };

  // Per-line calculation
  const calculateItemAmounts = (item, lineIndex = -1) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const gross = roundMoney(quantity * unitPrice);
    const originalQuantity = Number(item.original_quantity || item.quantity || 0);

    const discount = discounts.find(
      (row) => String(row._id) === String(purchaseAdjustments.discount_id),
    );
    const tax = taxes.find(
      (row) => String(row._id) === String(purchaseAdjustments.tax_id),
    );

    let selectedDiscountAmount = 0;
    if (discount?.discount_type === "percentage") {
      selectedDiscountAmount = roundMoney(
        (gross * Number(discount.value || 0)) / 100,
      );
    } else if (discount?.discount_type === "fixed") {
      selectedDiscountAmount = allocateDocumentAmount(
        discount.value,
        lineIndex,
        itemsToReceive,
      );
    }

    const manualDiscountAmount = allocateDocumentAmount(
      purchaseAdjustments.manual_discount,
      lineIndex,
      itemsToReceive,
    );

    const discountAmount = roundMoney(
      Math.min(gross, selectedDiscountAmount + manualDiscountAmount),
    );
    const afterDiscount = roundMoney(Math.max(gross - discountAmount, 0));

    let selectedTaxAmount = 0;
    let exclusiveTax = 0;
    let taxInclusive = false;

    if (tax?.tax_type === "percentage") {
      const rate = Number(tax.value || 0);
      taxInclusive = Boolean(tax.price_inclusive);
      selectedTaxAmount = taxInclusive
        ? roundMoney(afterDiscount - afterDiscount / (1 + rate / 100))
        : roundMoney((afterDiscount * rate) / 100);
      exclusiveTax = taxInclusive ? 0 : selectedTaxAmount;
    } else if (tax?.tax_type === "fixed") {
      taxInclusive = Boolean(tax.price_inclusive);
      selectedTaxAmount = allocateDocumentAmount(
        tax.value,
        lineIndex,
        itemsToReceive,
      );
      exclusiveTax = selectedTaxAmount;
    }

    const taxAmount = roundMoney(selectedTaxAmount);

    void originalQuantity;

    return {
      gross,
      discountAmount,
      taxAmount,
      exclusiveTax,
      taxInclusive,
      total: roundMoney(afterDiscount + exclusiveTax),
      discountPercent:
        discount?.discount_type === "percentage"
          ? Number(discount.value || 0)
          : 0,
      discountFlat: 0,
    };
  };

  useEffect(() => {
    const loadPage = async () => {
      const methods = await fetchPaymentMethods();
      await fetchPurchaseOrder(methods);
    };
    loadPage();
  }, [id]);

  const fetchPurchaseOrder = async (loadedMethods = []) => {
    try {
      setLoading(true);

      const response = await axios.get(`${url}/api/purchaseorders/${id}`);

      if (response.data.success) {
        const order = response.data.data;
        setPurchaseOrder(order);

        const initialItems = order.items
          .map((item) => {
            const remaining = item.quantity - (item.received_quantity || 0);
            return {
              product_id: item.product_id,
              quantity: remaining,
              unit_price: item.unit_price,
              discount_id:
                item.discount_id?._id || item.discount_id ||
                order.discount_id?._id || order.discount_id || "",
              tax_id:
                item.tax_id?._id || item.tax_id ||
                order.tax_id?._id || order.tax_id || "",
              discount_percent: Number(item.discount_percent || 0),
              discount_flat: Number(item.discount_flat || 0),
              discount_amount: Number(item.discount_amount || 0),
              tax_rate: Number(item.tax_rate || 0),
              tax_amount: Number(item.tax_amount || 0),
              tax_inclusive: Boolean(item.tax_inclusive),
              original_quantity: item.quantity,
              received_quantity: item.received_quantity || 0,
              remaining,
            };
          })
          .filter((item) => item.remaining > 0);

        setItemsToReceive(initialItems);

        setShippingCost(Number(order.shipping_cost || 0));
        setOtherCharges(Number(order.other_charges || 0));

        setPurchaseAdjustments({
          discount_id: order.discount_id?._id || order.discount_id || "",
          tax_id: order.tax_id?._id || order.tax_id || "",
          manual_discount: String(order.manual_discount || 0),
        });

        const existingPaid = order.total_amount_paid || 0;
        const existingPaymentDetails = order.payment_details || [];
        setExistingPaymentTotal(existingPaid);

        if (existingPaid > 0 && existingPaymentDetails.length > 0) {
          const formattedExistingPayments = existingPaymentDetails.map((p) => ({
            method: normalizeMethodKey(p.method),
            account_id:
              p.account_id?._id || p.account_id ||
              p.account?._id || p.account || "",
            account_name:
              p.account_name || p.account_id?.account_name ||
              p.account?.account_name || "",
            amount: Number(p.amount || 0),
            reference: p.reference || p.reference_no || "",
            notes: p.notes || "",
            source: p.source || "purchase_order",
            is_existing: true,
          }));
          setExistingPayments(formattedExistingPayments);
        }

        setNewPayments([
          {
            method: "credit",
            account_id: "",
            amount: "",
            reference: "",
            notes: "",
            is_existing: false,
          },
        ]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load purchase order");
      navigate("/ViewPurchaseOrder");
    } finally {
      setLoading(false);
    }
  };

  // Totals — computed from purchaseAdjustments + editable charges
  useEffect(() => {
    let subTotal = 0;
    let selectedDiscountAmount = 0;
    let manualDiscountAmount = 0;
    let selectedTaxAmount = 0;
    let selectedTaxExclusiveAmount = 0;

    itemsToReceive.forEach((item, idx) => {
      const calc = calculateItemAmounts(item, idx);
      subTotal = roundMoney(subTotal + calc.gross);
      selectedDiscountAmount = roundMoney(selectedDiscountAmount + calc.discountAmount);
      selectedTaxAmount = roundMoney(selectedTaxAmount + calc.taxAmount);
      selectedTaxExclusiveAmount = roundMoney(
        selectedTaxExclusiveAmount + calc.exclusiveTax,
      );
      manualDiscountAmount = roundMoney(
        manualDiscountAmount +
          Math.min(
            Math.max(0, Number(purchaseAdjustments.manual_discount) || 0),
            calc.gross,
          ),
      );
    });

    const manualDiscountDisplay = roundMoney(
      Math.max(0, Number(purchaseAdjustments.manual_discount) || 0),
    );

    const totalDiscount = roundMoney(
      selectedDiscountAmount,
    );

    const taxableAmount = roundMoney(
      Math.max(subTotal - totalDiscount, 0),
    );

    const shipping = roundMoney(Math.max(0, Number(shippingCost) || 0));
    const other = roundMoney(Math.max(0, Number(otherCharges) || 0));

    const grandTotal = roundMoney(
      taxableAmount + selectedTaxExclusiveAmount + shipping + other,
    );

    const newPaymentsTotal = newPayments.reduce(
      (sum, payment) =>
        normalizeMethodKey(payment.method) === "credit"
          ? sum
          : sum + (Number(payment.amount) || 0),
      0,
    );

    setNewPaymentTotal(roundMoney(newPaymentsTotal));

    const totalPaid = roundMoney(
      Number(existingPaymentTotal || 0) + newPaymentsTotal,
    );

    setTotals({
      subTotal,
      selectedDiscountAmount,
      manualDiscountAmount: manualDiscountDisplay,
      totalDiscount,
      taxableAmount,
      selectedTaxAmount,
      totalTax: selectedTaxAmount,
      shippingCost: shipping,
      otherCharges: other,
      grandTotal,
      totalAmountPaid: totalPaid,
      netAmount: roundMoney(Math.max(grandTotal - totalPaid, 0)),
    });

    void manualDiscountAmount;
    void selectedTaxExclusiveAmount;
  }, [
    purchaseOrder,
    itemsToReceive,
    newPayments,
    existingPaymentTotal,
    shippingCost,
    otherCharges,
    purchaseAdjustments,
    discounts,
    taxes,
  ]);

  // Payment handlers
  const addPaymentRow = () => {
    const defaultMethod =
      getImmediatePaymentMethods()[0] ||
      paymentMethods.find((method) => normalizeMethodKey(method.key) === "cash") ||
      DEFAULT_PAYMENT_METHODS.find((method) => method.key === "cash");

    setNewPayments((current) => [
      ...current,
      {
        method: defaultMethod?.key || "cash",
        account_id:
          defaultMethod?.accounts?.[0]?._id ||
          defaultMethod?.accounts?.[0]?.value || "",
        amount: "",
        reference: "",
        notes: "",
        is_existing: false,
      },
    ]);
  };

  const removePaymentRow = (index) => {
    if (newPayments.length > 1) {
      setNewPayments(newPayments.filter((_, i) => i !== index));
    } else {
      const updated = [...newPayments];
      updated[index].amount = "";
      setNewPayments(updated);
    }
  };

  const handlePaymentChange = (index, field, value) => {
    const updated = [...newPayments];
    if (field === "amount") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        updated[index][field] = value;
      }
    } else {
      updated[index][field] = value;
    }
    setNewPayments(updated);
  };

  const handleQuantityChange = (index, quantity) => {
    const updated = [...itemsToReceive];
    const maxQty = updated[index].remaining;
    let newQty = parseFloat(quantity);
    if (isNaN(newQty)) newQty = 0;
    if (newQty > maxQty) newQty = maxQty;
    if (newQty < 0) newQty = 0;
    updated[index].quantity = newQty;
    setItemsToReceive(updated);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    const updated = itemsToReceive.map((item) => ({
      ...item,
      quantity: checked ? item.remaining : 0,
    }));
    setItemsToReceive(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const itemsToReceiveFiltered = itemsToReceive.filter((item) => item.quantity > 0);
    if (itemsToReceiveFiltered.length === 0) {
      toast.error("No items selected for receiving");
      return;
    }

    const validNewPayments = newPayments.filter(
      (payment) =>
        normalizeMethodKey(payment.method) !== "credit" &&
        payment.amount && Number(payment.amount) > 0,
    );

    for (const payment of validNewPayments) {
      if (!payment.method) {
        toast.error("Please select a payment method");
        return;
      }
      if (!payment.account_id) {
        toast.error("Please select an account for this payment method");
        return;
      }
    }

    const enteredPaymentTotal = validNewPayments.reduce(
      (sum, payment) => sum + (Number(payment.amount) || 0),
      0,
    );

    const maximumAdditionalPayment = Math.max(
      totals.grandTotal - Number(existingPaymentTotal || 0),
      0,
    );

    // ✅ UPDATED: better overpayment message with amounts
    if (enteredPaymentTotal > maximumAdditionalPayment + 0.01) {
      const excess = Math.round((enteredPaymentTotal - maximumAdditionalPayment) * 100) / 100;
      toast.error(
        `Payment exceeds the balance due by ${formatCurrency(excess)}. ` +
        `Balance due: ${formatCurrency(maximumAdditionalPayment)} ` +
        `(Grand Total: ${formatCurrency(totals.grandTotal)} − Prepaid: ${formatCurrency(existingPaymentTotal)}) • ` +
        `Entered: ${formatCurrency(enteredPaymentTotal)}. ` +
        `Reduce the payment by ${formatCurrency(excess)} to continue.`
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        items_to_receive: itemsToReceiveFiltered.map((item) => {
          const originalIndex = itemsToReceive.findIndex((row) => row === item);
          const amounts = calculateItemAmounts(item, originalIndex);

          return {
            product_id: item.product_id._id || item.product_id,
            quantity: Number(item.quantity || 0),
            unit_price: Number(item.unit_price || 0),
            discount_id: purchaseAdjustments.discount_id || null,
            tax_id: purchaseAdjustments.tax_id || null,
            discount_percent: 0,
            discount_flat: 0,
            discount_amount: Number(amounts.discountAmount || 0),
            tax_rate: 0,
            tax_amount: Number(amounts.taxAmount || 0),
            total_price: Number(amounts.total || 0),
          };
        }),

        payments: validNewPayments.map((payment) => ({
          method: normalizeMethodKey(payment.method),
          account_id: payment.account_id,
          amount: Number(payment.amount || 0),
          reference: payment.reference || "",
          notes: payment.notes || "",
        })),

        discount_id: purchaseAdjustments.discount_id || null,
        tax_id: purchaseAdjustments.tax_id || null,
        manual_discount: Number(purchaseAdjustments.manual_discount) || 0,

        shipping_cost: Number(shippingCost) || 0,
        other_charges: Number(otherCharges) || 0,

        create_purchase_invoice: true,
        affect_inventory: true,
        post_financial_entries: true,
        payment_terms: "credit",
        accounts_payable_automatic: true,

        credit_amount: Math.max(
          Number(totals.grandTotal || 0) -
            Number(existingPaymentTotal || 0) -
            Number(enteredPaymentTotal || 0),
          0,
        ),

        invoice_totals: {
          subtotal: Number(totals.subTotal || 0),
          discount: Number(totals.totalDiscount || 0),
          tax: Number(totals.totalTax || 0),
          shipping: Number(totals.shippingCost || 0),
          other: Number(totals.otherCharges || 0),
          grand_total: Number(totals.grandTotal || 0),
        },
      };

      const response = await axios.post(
        `${url}/api/purchaseorders/${id}/convert`,
        payload,
      );

      if (response.data.success) {
        toast.success(`Order converted to GRN successfully!`);
        setTimeout(() => navigate("/purchase/invoices"), 1500);
      }
    } catch (error) {
      const status = error.response?.status;
      const message =
        error.response?.data?.message || error.message || "Failed to convert order";
      if (status === 401 || status === 403) {
        toast.error(`${message} Please log in again.`);
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Overpayment detection — raw entered new payments vs balance due
  const balanceDueBeforeNew = Math.max(
    totals.grandTotal - Number(existingPaymentTotal || 0),
    0
  );
  const overpaidAmount = Math.max(
    0,
    newPaymentTotal - balanceDueBeforeNew
  );
  const hasOverpayment = overpaidAmount > 0.01;

  if (loading) {
    return (
      <div className="convert-container">
        <div className="loading-state">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading purchase order...</p>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="convert-container">
        <div className="error-state">
          <AlertCircle size={48} color="#ff4d4f" />
          <h3>Order Not Found</h3>
          <button onClick={() => navigate("/ViewPurchaseOrders")} className="back-btn">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="convert-container">
      <div className="convert-header">
        <button onClick={() => navigate("/ViewPurchaseOrders")} className="back-btn">
          <X size={20} />
        </button>
        <div className="header-title">
          <Receipt size={24} />
          <h2>Convert Order to GRN</h2>
          <span style={{ fontSize: "12px", color: "#52c41a", marginLeft: "10px" }}>
            Goods Receipt Note
          </span>
        </div>
      </div>

      {/* Order Summary */}
      <div className="order-summary">
        <h3>Order Information</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Reference ID:</span>
            <span className="value">{purchaseOrder.reference_id}</span>
          </div>
          <div className="summary-item">
            <span className="label">Order No:</span>
            <span className="value">{purchaseOrder.order_no}</span>
          </div>
          <div className="summary-item">
            <span className="label">Supplier:</span>
            <span className="value">{purchaseOrder.supplier_id?.supplier_name || "N/A"}</span>
          </div>
          <div className="summary-item">
            <span className="label">Order Date:</span>
            <span className="value">{new Date(purchaseOrder.order_date).toLocaleDateString()}</span>
          </div>
          <div className="summary-item">
            <span className="label">Warehouse:</span>
            <span className="value">{purchaseOrder.warehouse_id?.warehouse_name || "N/A"}</span>
          </div>
          <div className="summary-item">
            <span className="label">Order Status:</span>
            <span className={`status-badge ${purchaseOrder.status}`}>
              {purchaseOrder.status?.replace("_", " ")}
            </span>
          </div>
          {existingPaymentTotal > 0 && (
            <div className="summary-item highlight">
              <span className="label">Prepaid Amount:</span>
              <span className="value prepaid">{formatCurrency(existingPaymentTotal)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Items to Receive */}
      <div className="items-section">
        <div className="section-header">
          <h3>Items to Receive</h3>
          <label className="select-all">
            <input
              type="checkbox"
              onChange={handleSelectAll}
              checked={
                itemsToReceive.length > 0 &&
                itemsToReceive.every(
                  (item) => Number(item.quantity) === Number(item.remaining),
                )
              }
            />
            Select All
          </label>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              minWidth: 1050,
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ ...receiveTh, width: "22%" }}>Product</th>
                <th style={{ ...receiveTh, width: "8%" }}>Ordered</th>
                <th style={{ ...receiveTh, width: "8%" }}>Received</th>
                <th style={{ ...receiveTh, width: "9%" }}>Remaining</th>
                <th style={{ ...receiveTh, width: "10%" }}>To Receive</th>
                <th style={{ ...receiveTh, width: "10%" }}>Unit Price</th>
                <th style={{ ...receiveTh, width: "11%" }}>Discount</th>
                <th style={{ ...receiveTh, width: "10%" }}>Tax</th>
                <th style={{ ...receiveTh, width: "12%" }}>Total</th>
              </tr>
            </thead>

            <tbody>
              {itemsToReceive.map((item, idx) => {
                const calculated = calculateItemAmounts(item, idx);
                return (
                  <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={receiveTd}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Package size={14} color="#1890ff" />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {item.product_id?.product_name || "Unknown"}
                          </div>
                          <div style={{ fontSize: 11, color: "#777" }}>
                            {item.product_id?.product_code || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={receiveTd}>{item.original_quantity}</td>
                    <td style={receiveTd}>{item.received_quantity}</td>
                    <td style={receiveTd}>{item.remaining}</td>

                    <td style={receiveTd}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(event) =>
                          handleQuantityChange(idx, event.target.value)
                        }
                        min="0"
                        max={item.remaining}
                        step="1"
                        style={{
                          width: "100%",
                          padding: 8,
                          border: "1px solid #ddd",
                          borderRadius: 4,
                        }}
                      />
                    </td>

                    <td style={receiveTd}>{formatCurrency(item.unit_price)}</td>

                    <td style={receiveTd}>
                      {calculated.discountAmount > 0
                        ? `${formatCurrency(calculated.discountAmount)}`
                        : "-"}
                    </td>

                    <td style={receiveTd}>
                      {calculated.taxAmount > 0
                        ? `${formatCurrency(calculated.taxAmount)}`
                        : "-"}
                    </td>

                    <td style={{ ...receiveTd, fontWeight: 700, color: "#1890ff" }}>
                      {formatCurrency(calculated.total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Existing Payments */}
      {existingPayments.length > 0 && (
        <div className="payments-section existing-payments">
          <h3>Prepaid Amount (Paid During Order Creation)</h3>
          <div className="existing-payments-list">
            {existingPayments.map((payment, idx) => (
              <div
                key={idx}
                className="payment-row existing"
                style={{
                  background: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <span style={{ fontWeight: 500, textTransform: "capitalize" }}>
                    {payment.method}
                  </span>
                  <span>
                    Account:{" "}
                    {payment.account_name ||
                      findPaymentAccount(payment)?.account_name ||
                      "Account not stored"}
                  </span>
                  <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                    {formatCurrency(payment.amount)}
                  </span>
                  {payment.reference && <span>Ref: {payment.reference}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle size={16} color="#52c41a" />
                  <span style={{ fontSize: "12px", color: "#52c41a" }}>Prepaid</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Discount and Tax */}
      <div className="payments-section" style={{ marginTop: 20 }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Banknote size={18} />
          Order Discount and Tax
        </h3>

        <div
          style={{
            marginBottom: 14,
            padding: "10px 12px",
            background: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          These are pre-filled from the purchase order. You can change them
          for this GRN — the totals above will update automatically.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div>
            <label style={paymentLabel}>Apply Discount to All Items</label>
            <select
              value={purchaseAdjustments.discount_id}
              onChange={(event) =>
                setPurchaseAdjustments((prev) => ({
                  ...prev,
                  discount_id: event.target.value,
                }))
              }
              style={paymentControl}
            >
              <option value="">No Discount</option>
              {discounts.map((discount) => (
                <option key={discount._id} value={discount._id}>
                  {discount.name} ({discount.code}) -{" "}
                  {discount.discount_type === "percentage"
                    ? `${discount.value}%`
                    : formatCurrency(discount.value)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={paymentLabel}>Apply Tax to All Items</label>
            <select
              value={purchaseAdjustments.tax_id}
              onChange={(event) =>
                setPurchaseAdjustments((prev) => ({
                  ...prev,
                  tax_id: event.target.value,
                }))
              }
              style={paymentControl}
            >
              <option value="">No Tax</option>
              {taxes.map((tax) => (
                <option key={tax._id} value={tax._id}>
                  {tax.name} ({tax.code}) -{" "}
                  {tax.tax_type === "percentage"
                    ? `${tax.value}%`
                    : formatCurrency(tax.value)}
                  {" · "}
                  {tax.price_inclusive ? "Inclusive" : "Exclusive"}
                  {tax.recoverable ? " · Recoverable" : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={paymentLabel}>Manual Discount Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={purchaseAdjustments.manual_discount}
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                  setPurchaseAdjustments((prev) => ({
                    ...prev,
                    manual_discount: raw,
                  }));
                }
              }}
              placeholder="Example: 5.00"
              style={paymentControl}
            />
            <small style={{ color: "#666" }}>
              Added to the selected discount and allocated across items.
            </small>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="payments-section">
        <h3>Payment Details</h3>

        <div
          style={{
            marginBottom: 14,
            padding: "10px 12px",
            background: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Select Credit, Cash, Bank, Mobile Money, or Other. You can pay the
          full invoice, pay part of it, or leave the remaining amount in
          Accounts Payable.
        </div>

        {newPayments.map((payment, idx) => {
          const method = normalizeMethodKey(payment.method || "credit");
          const isCredit = method === "credit";
          const accounts = getAccountsForMethod(method);

          return (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 2fr 1fr 1.3fr auto",
                gap: 10,
                alignItems: "end",
                marginBottom: 12,
                padding: 12,
                border: "1px solid #eee",
                borderRadius: 6,
                background: "#fafafa",
              }}
            >
              <div>
                <label style={paymentLabel}>Payment Method</label>
                <select
                  value={method}
                  onChange={(event) => {
                    const nextMethod = normalizeMethodKey(event.target.value);
                    const firstMethodAccount = getAccountsForMethod(nextMethod)[0];
                    const firstAccount =
                      firstMethodAccount?._id || firstMethodAccount?.value || "";
                    setNewPayments((previous) =>
                      previous.map((row, rowIndex) =>
                        rowIndex === idx
                          ? {
                              ...row,
                              method: nextMethod,
                              account_id: nextMethod === "credit" ? "" : firstAccount,
                              amount: nextMethod === "credit" ? "" : row.amount,
                            }
                          : row,
                      ),
                    );
                  }}
                  style={paymentControl}
                >
                  {(paymentMethods.length ? paymentMethods : DEFAULT_PAYMENT_METHODS).map(
                    (methodOption) => (
                      <option key={methodOption.key} value={methodOption.key}>
                        {methodOption.name}
                      </option>
                    ),
                  )}
                </select>
              </div>

              <div>
                <label style={paymentLabel}>Account</label>
                {isCredit ? (
                  <input
                    type="text"
                    value="Accounts Payable (automatic)"
                    readOnly
                    style={{ ...paymentControl, background: "#f5f5f5" }}
                  />
                ) : (
                  <select
                    value={payment.account_id || ""}
                    onChange={(event) =>
                      handlePaymentChange(idx, "account_id", event.target.value)
                    }
                    style={paymentControl}
                  >
                    <option value="">
                      {accounts.length ? "Select Account" : "No account configured"}
                    </option>
                    {accounts.map((account) => (
                      <option
                        key={account._id || account.value}
                        value={account._id || account.value}
                      >
                        {account.account_code ? `${account.account_code} - ` : ""}
                        {account.account_name || account.name || "Account"}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label style={paymentLabel}>
                  {isCredit ? "Credit Balance" : "Amount"}
                </label>
                <input
                  type={isCredit ? "text" : "number"}
                  value={isCredit ? formatCurrency(totals.netAmount) : payment.amount}
                  onChange={(event) =>
                    !isCredit && handlePaymentChange(idx, "amount", event.target.value)
                  }
                  readOnly={isCredit}
                  min="0"
                  max={Math.max(
                    totals.grandTotal - Number(existingPaymentTotal || 0),
                    0,
                  )}
                  step="0.01"
                  placeholder={isCredit ? "" : "0.00"}
                  style={{
                    ...paymentControl,
                    ...(isCredit ? { background: "#f5f5f5" } : {}),
                  }}
                />
              </div>

              <div>
                <label style={paymentLabel}>Reference</label>
                <input
                  type="text"
                  value={payment.reference || ""}
                  onChange={(event) =>
                    handlePaymentChange(idx, "reference", event.target.value)
                  }
                  disabled={isCredit}
                  placeholder="Optional"
                  style={paymentControl}
                />
              </div>

              <button
                type="button"
                onClick={() => removePaymentRow(idx)}
                disabled={newPayments.length === 1}
                style={{
                  padding: 9,
                  border: "1px solid #ffccc7",
                  borderRadius: 4,
                  background: "#fff1f0",
                  color: "#ff4d4f",
                  cursor: newPayments.length === 1 ? "not-allowed" : "pointer",
                }}
                title="Remove payment row"
              >
                <Trash2 size={15} />
              </button>

              {!isCredit && accounts.length === 0 && (
                <div style={{ gridColumn: "1 / -1", color: "#fa8c16", fontSize: 12 }}>
                  Configure an active manual-posting account for this
                  payment method before submitting it.
                </div>
              )}
            </div>
          );
        })}

        {getImmediatePaymentMethods().length === 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              border: "1px dashed #faad14",
              borderRadius: 6,
              background: "#fffbe6",
              color: "#8c5a00",
              fontSize: 12,
            }}
          >
            No payment account was returned. Confirm that the backend
            was restarted and that GET /api/account/payment-methods returns
            active Cash, Bank, Mobile Money/EVC, or Other Asset accounts.
          </div>
        )}

        <button type="button" onClick={addPaymentRow} className="add-payment">
          <Plus size={16} />
          Add Another Payment Method
        </button>

        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: 6,
          }}
        >
          Remaining Accounts Payable:{" "}
          <strong>{formatCurrency(totals.netAmount)}</strong>
        </div>
      </div>

      {/* Additional Charges */}
      <div className="payments-section" style={{ marginTop: 20 }}>
        <h3>Additional Charges</h3>

        <div
          style={{
            marginBottom: 14,
            padding: "10px 12px",
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Shipping and other charges are pre-filled from the purchase order.
          You can adjust them for this GRN before converting.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label style={paymentLabel}>Shipping Cost</label>
            <input
              type="number"
              value={shippingCost}
              onChange={(event) => {
                const raw = event.target.value;
                setShippingCost(raw === "" ? "" : Math.max(0, Number(raw) || 0));
              }}
              min="0"
              step="0.01"
              placeholder="0.00"
              style={paymentControl}
            />
          </div>

          <div>
            <label style={paymentLabel}>Other Charges</label>
            <input
              type="number"
              value={otherCharges}
              onChange={(event) => {
                const raw = event.target.value;
                setOtherCharges(raw === "" ? "" : Math.max(0, Number(raw) || 0));
              }}
              min="0"
              step="0.01"
              placeholder="0.00"
              style={paymentControl}
            />
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="totals-section">
        <div className="total-row">
          <span>Subtotal:</span>
          <strong>{formatCurrency(totals.subTotal)}</strong>
        </div>
        <div className="total-row">
          <span>Selected Discount:</span>
          <strong className="discount">
            -{formatCurrency(totals.selectedDiscountAmount)}
          </strong>
        </div>
        <div className="total-row">
          <span>Manual Discount:</span>
          <strong className="discount">
            -{formatCurrency(totals.manualDiscountAmount)}
          </strong>
        </div>
        <div className="total-row">
          <span>Total Discount:</span>
          <strong className="discount">
            -{formatCurrency(totals.totalDiscount)}
          </strong>
        </div>
        <div className="total-row">
          <span>Selected Tax:</span>
          <strong>+{formatCurrency(totals.selectedTaxAmount)}</strong>
        </div>
        <div className="total-row">
          <span>Total Tax:</span>
          <strong>+{formatCurrency(totals.totalTax)}</strong>
        </div>

        {totals.shippingCost > 0 && (
          <div className="total-row">
            <span>Shipping:</span>
            <strong>+{formatCurrency(totals.shippingCost)}</strong>
          </div>
        )}
        {totals.otherCharges > 0 && (
          <div className="total-row">
            <span>Other Charges:</span>
            <strong>+{formatCurrency(totals.otherCharges)}</strong>
          </div>
        )}

        <div className="grand-total">
          <span>Grand Total:</span>
          <strong>{formatCurrency(totals.grandTotal)}</strong>
        </div>
        {existingPaymentTotal > 0 && (
          <div className="total-row prepaid-row">
            <span>Prepaid Amount:</span>
            <strong className="prepaid">-{formatCurrency(existingPaymentTotal)}</strong>
          </div>
        )}
        {newPaymentTotal > 0 && (
          <div className="total-row">
            <span>Additional Payment:</span>
            <strong className="paid">{formatCurrency(newPaymentTotal)}</strong>
          </div>
        )}
        <div className="net-total">
          <span>Net Amount (Balance Due):</span>
          <strong className={totals.netAmount > 0 ? "balance-due" : "overpaid"}>
            {formatCurrency(totals.netAmount)}
          </strong>
        </div>
      </div>

      {/* ✅ Overpayment warning — shown above the buttons */}
      {hasOverpayment && (
        <div
          style={{
            marginBottom: 15,
            padding: '12px 16px',
            background: '#fff1f0',
            border: '1px solid #ffa39e',
            borderLeft: '4px solid #f5222d',
            borderRadius: 4,
            color: '#cf1322',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>
              Payment amount cannot exceed the balance due.
            </strong>
            <div style={{ marginBottom: 4 }}>
              Balance Due: <strong>{formatCurrency(balanceDueBeforeNew)}</strong>
              {" • "}
              Entered: <strong>{formatCurrency(newPaymentTotal)}</strong>
              {" • "}
              Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
              (Grand Total {formatCurrency(totals.grandTotal)}
              {existingPaymentTotal > 0 && ` − Prepaid ${formatCurrency(existingPaymentTotal)}`}
              {" = "}{formatCurrency(balanceDueBeforeNew)})
            </div>
            <div>
              Reduce the payment by{" "}
              <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="actions">
        <button
          type="button"
          onClick={() => navigate("/ViewPurchaseOrder")}
          className="cancel-btn"
        >
          <X size={16} /> Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={
            submitting ||
            hasOverpayment ||
            itemsToReceive.every((item) => item.quantity === 0)
          }
          title={
            hasOverpayment
              ? `Payment exceeds balance due by ${formatCurrency(overpaidAmount)}`
              : ""
          }
          className="convert-btn"
          style={
            hasOverpayment
              ? { backgroundColor: '#d9d9d9', cursor: 'not-allowed' }
              : undefined
          }
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              Converting...
            </>
          ) : (
            <>
              <ArrowRight size={16} />
              Convert to GRN
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const receiveTh = {
  padding: "12px 10px",
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  fontSize: 12,
};

const receiveTd = {
  padding: "12px 10px",
  verticalAlign: "middle",
  fontSize: 13,
};

const paymentLabel = {
  display: "block",
  marginBottom: 4,
  fontSize: 12,
  color: "#666",
};

const paymentControl = {
  width: "100%",
  padding: 8,
  border: "1px solid #ddd",
  borderRadius: 4,
  boxSizing: "border-box",
};

export default ConvertOrderToGRN;